import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useMediaQuery } from "react-responsive";

import { Galleria } from "primereact/galleria";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { SplitButton } from "primereact/splitbutton";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import { Tooltip } from "primereact/tooltip";
import { APIProvider, Map, Marker, InfoWindow } from "@vis.gl/react-google-maps";

import env from "../../Config/env";
import dream4Logo from "../../Assets/Images/logoHeader.svg";
import imagePlaceHolder from "../../Assets/Images/noimg.jpg";
import { getPublicHotel } from "../../Services/publicHotel";
import { downloadHotelPdf, fetchHotelPdfUrl, viewHotelPdf } from "../../Utils/hotelPdf";

const PURPLE = "#814DE5";
const DARK = "#2E0249";
const DESC_LIMIT = 600;

const photoUrl = (p) => (typeof p === "string" ? p : (p?.photo_url || p?.urls?.default || p?.url || null));

// A foto grande vem em baixa resolução (path com /maxNNN/, ex.: max500 → borrada).
// Para a imagem principal, subimos para max800 (o CDN aceita). URLs sem esse padrão
// ficam intactas.
const bigPhotoUrl = (url, size = 800) => (typeof url === "string" ? url.replace(/\/max\d+\//, `/max${size}/`) : url);

// Wrapper visual em escopo de MÓDULO (estável — se ficar dentro do componente,
// re-renders recriam o Shell e o React remonta a árvore toda, quebrando galeria/modais/mapa).
const Shell = ({ agencyLogo, agencia, actions, children }) => (
	<div style={{ minHeight: "100vh", background: "#F2F4F7", padding: "1.5rem", display: "flex", justifyContent: "center" }}>
		<div style={{ width: "min(960px, 100%)" }}>
			{/* Header: só a marca Dream4. */}
			<div style={{ marginBottom: "1rem" }}>
				<img src={dream4Logo} alt="Dream4" style={{ height: "34px" }} />
			</div>
			{/* Toolbar: agência (esq) + ações/PDF (dir). */}
			{(agencyLogo || agencia || actions) && (
				<div style={{ background: "#fff", borderRadius: "12px", padding: "0.75rem 1rem", boxShadow: "0 2px 14px rgba(0,0,0,0.06)", marginBottom: "1rem", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap" }}>
					<div style={{ display: "flex", alignItems: "center", gap: "0.6rem", minWidth: 0 }}>
						{agencyLogo && <img src={agencyLogo} alt="Agência" style={{ maxHeight: "40px", maxWidth: "150px", objectFit: "contain" }} onError={(e) => { e.target.style.display = "none"; }} />}
						{agencia && <span style={{ fontWeight: 600, color: DARK }}>{agencia}</span>}
					</div>
					{actions && <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>{actions}</div>}
				</div>
			)}
			{/* Card de detalhes do hotel. */}
			<div style={{ background: "#fff", borderRadius: "16px", padding: "1.5rem", boxShadow: "0 2px 14px rgba(0,0,0,0.06)", textAlign: "left" }}>
				{children}
			</div>
			<div style={{ textAlign: "center", color: "#9aa0a6", fontSize: "0.75rem", marginTop: "1rem" }}>
				Informações do hotel apresentadas pela Dream4{agencia ? ` em parceria com ${agencia}` : ""}.
			</div>
		</div>
	</div>
);

/**
 * HotelViewPublic (/hotel/:token) — página PÚBLICA (sem login, sem SEO), fora do
 * layout. Token OPACO: a URL/página NUNCA revelam o fornecedor. Branding em 2 modos:
 * com agência (?a=<token>) ou só Dream4. Mostra nome/cidade/país/nota, galeria
 * (miniaturas trocam a foto), descrição, comodidades, arredores (POIs + modal),
 * mapa (ampliar) e PDF da propriedade. SEM abas de quartos/localização/termos e
 * SEM coluna transacional. Dados via API (Services/publicHotel).
 */
const HotelViewPublic = () => {
	const { token } = useParams();
	const [sp] = useSearchParams();
	// ?a=<token opaco da agência> (ex.: "companyId:assinatura" ou JWT) — o backend
	// valida/resolve; NÃO é um id trocável (evita ver a branding de outra agência).
	const agencyParam = sp.get("a");
	const isTabletOrMobile = useMediaQuery({ query: "(max-width: 1023px)" });

	const [hotel, setHotel] = useState(null);
	const [loading, setLoading] = useState(true);
	const [descOpen, setDescOpen] = useState(false);
	const [activeIndex, setActiveIndex] = useState(0);
	const [mapOpen, setMapOpen] = useState(false);
	const [poisOpen, setPoisOpen] = useState(false);
	const [selectedPoi, setSelectedPoi] = useState(null);
	const [loadedSrcs, setLoadedSrcs] = useState({});
	const [pdfOpen, setPdfOpen] = useState(false);
	const [pdfUrl, setPdfUrl] = useState(null);
	const [pdfBusy, setPdfBusy] = useState(false);
	const toast = useRef(null);

	useEffect(() => {
		let alive = true;
		setLoading(true);
		getPublicHotel(token, agencyParam)
			.then((h) => { if (alive) setHotel(h); })
			.catch(() => { if (alive) setHotel(null); })
			.finally(() => { if (alive) setLoading(false); });
		return () => { alive = false; };
	}, [token, agencyParam]);

	const agencyLogo = hotel?.agencia?.logoUrl || null;
	const agencia = hotel?.agencia?.nome || null;

	if (loading) {
		return <Shell agencyLogo={agencyLogo} agencia={agencia}><div style={{ textAlign: "center", padding: "2rem" }}><ProgressSpinner /></div></Shell>;
	}
	if (!hotel) {
		return (
			<Shell agencyLogo={agencyLogo} agencia={agencia}>
				<div style={{ textAlign: "center", padding: "1.5rem" }}>
					<i className="pi pi-exclamation-triangle" style={{ fontSize: "2rem", color: "#c0392b" }} />
					<h2>Hotel não encontrado</h2>
					<p style={{ color: "#6B6B6B" }}>Não foi possível carregar os dados deste hotel.</p>
				</div>
			</Shell>
		);
	}

	const fotos = (hotel.photos || []).filter((p) => photoUrl(p));
	const pois = hotel.poisProximos || hotel.pois || [];
	const local = [hotel.city, hotel.country].filter(Boolean).join(", ");
	// Nº de estrelas de categoria (tolerante ao nome do campo até o BE fixar; ver handoff de estrelas).
	const stars = (() => { const n = parseInt(hotel.stars ?? hotel.estrelas ?? hotel.star_rating ?? hotel.hotel_class, 10); return (n >= 1 && n <= 5) ? n : 0; })();
	const desc = hotel.property_descriptionPT || hotel.property_description || "";
	const descLong = desc.length > DESC_LIMIT;
	const distancia = hotel.distanceToCityCenter
		? (hotel.distanceToCityCenter < 700 ? `${hotel.distanceToCityCenter} m do centro` : `${(hotel.distanceToCityCenter / 1000).toFixed(2)} km do centro`)
		: null;
	const center = (hotel.latitude != null && hotel.longitude != null) ? { lat: Number(hotel.latitude), lng: Number(hotel.longitude) } : null;

	// Galeria: imagem ocupa toda a largura (aspecto fixo → sem "pulo"); spinner enquanto carrega.
	const galleriaItem = (item) => {
		const src = bigPhotoUrl(photoUrl(item), 800);
		const ok = !!loadedSrcs[src];
		return (
			<div style={{ position: "relative", width: "100%", aspectRatio: "16 / 9", background: "#f0f0f0", borderRadius: "10px", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
				{!ok && <ProgressSpinner style={{ width: 44, height: 44, position: "absolute" }} strokeWidth="4" />}
				<img
					src={src}
					alt=""
					onLoad={() => setLoadedSrcs((m) => ({ ...m, [src]: true }))}
					onError={(e) => { e.target.src = imagePlaceHolder; setLoadedSrcs((m) => ({ ...m, [src]: true })); }}
					style={{ width: "100%", height: "100%", objectFit: "cover", opacity: ok ? 1 : 0, transition: "opacity 0.2s" }}
				/>
			</div>
		);
	};
	const handleDownloadPdf = () => {
		setPdfBusy(true);
		downloadHotelPdf(token, agencyParam)
			.catch(() => toast.current?.show({ severity: "error", summary: "Erro ao gerar PDF", detail: "Não foi possível gerar o PDF agora. Tente novamente.", life: 5000 }))
			.finally(() => setPdfBusy(false));
	};
	const handleViewPdf = () => {
		setPdfBusy(true);
		if (isTabletOrMobile) {
			viewHotelPdf(token, agencyParam)
				.catch(() => toast.current?.show({ severity: "error", summary: "Erro ao abrir o PDF", detail: "Não foi possível abrir o PDF agora. Tente novamente.", life: 5000 }))
				.finally(() => setPdfBusy(false));
			return;
		}
		fetchHotelPdfUrl(token, agencyParam)
			.then((url) => { setPdfUrl(url); setPdfOpen(true); })
			.catch(() => toast.current?.show({ severity: "error", summary: "Erro ao abrir o PDF", detail: "Não foi possível abrir o PDF agora. Tente novamente.", life: 5000 }))
			.finally(() => setPdfBusy(false));
	};
	const closePdfViewer = () => { setPdfOpen(false); setPdfUrl((u) => { if (u) URL.revokeObjectURL(u); return null; }); };

	const pdfActions = (
		<SplitButton label="Ver PDF" icon="pi pi-eye" outlined size="small" className="pdfSplit"
			onClick={handleViewPdf} disabled={pdfBusy}
			model={[{ label: "Baixar PDF", icon: "pi pi-download", command: handleDownloadPdf }]} />
	);

	return (
		<Shell agencyLogo={agencyLogo} agencia={agencia} actions={pdfActions}>
			<Toast ref={toast} position="bottom-center" />
			<Tooltip target=".nota-badge" position="left" />

			{/* Cabeçalho do hotel */}
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "1rem", flexWrap: "wrap", marginBottom: "1rem" }}>
				<div style={{ minWidth: 0 }}>
					<h1 style={{ color: DARK, fontSize: "1.5rem", margin: "0 0 0.25rem" }}>{hotel.hotel_name}</h1>
					{stars > 0 && (
						<div style={{ color: "#F5A623", fontSize: "1.15rem", letterSpacing: "2px", whiteSpace: "nowrap", margin: "0 0 0.25rem" }} aria-label={`${stars} estrelas`}>{"★".repeat(stars)}</div>
					)}
					<div style={{ color: "#6B6B6B" }}>
						<i className="pi pi-map-marker" style={{ color: PURPLE, marginRight: "0.35rem" }} />{local}
						{distancia ? <span style={{ marginLeft: "0.5rem" }}>· {distancia}</span> : null}
					</div>
				</div>
				{hotel.review_rating != null && (
					<div
						className="nota-badge"
						tabIndex={0}
						role="img"
						data-pr-tooltip="Nota média de avaliações de hóspedes em sites como Booking e similares."
						aria-label="Nota média de avaliações de hóspedes em sites como Booking e similares."
						style={{ background: "#00A3FF", color: "#fff", borderRadius: "12px", padding: "0.5rem 0.75rem", fontWeight: 700, fontSize: "1.1rem", minWidth: "3rem", textAlign: "center", cursor: "help", outlineColor: PURPLE }}
					>
						{Number(hotel.review_rating).toFixed(1)}
					</div>
				)}
			</div>

			{/* Galeria principal (largura total) */}
			{fotos.length > 0 && (
				<div style={{ position: "relative", marginBottom: "0.75rem" }}>
					<Galleria
						value={fotos}
						activeIndex={activeIndex}
						onItemChange={(e) => setActiveIndex(e.index)}
						circular
						showThumbnails={false}
						showItemNavigators
						showIndicators
						showIndicatorsOnItem
						indicatorsPosition="bottom"
						item={galleriaItem}
						style={{ maxWidth: "100%" }}
					/>
				</div>
			)}

			{/* Miniaturas */}
			{fotos.length > 1 && (
				<div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginBottom: "1.25rem" }}>
					{fotos.slice(0, 11).map((image, index) => (
						<div key={index} onClick={() => setActiveIndex(index)}
							style={{ width: "6.5rem", height: "5rem", borderRadius: "0.75rem", overflow: "hidden", cursor: "pointer", border: index === activeIndex ? `2px solid ${PURPLE}` : "2px solid transparent" }}>
							<img src={photoUrl(image)} onError={(e) => { e.target.src = imagePlaceHolder; }} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
						</div>
					))}
					{fotos.length > 11 && (
						<div onClick={() => setActiveIndex(11)}
							style={{ width: "6.5rem", height: "5rem", borderRadius: "0.75rem", background: "#814DE555", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontWeight: 600, color: DARK }}>
							+{fotos.length - 11} fotos
						</div>
					)}
				</div>
			)}

			{/* Descrição */}
			{desc && (
				<div style={{ marginBottom: "1.25rem" }}>
					<h3 style={{ color: DARK, marginBottom: "0.4rem" }}>Sobre o hotel</h3>
					<p style={{ color: "#444", lineHeight: 1.6, margin: 0 }}>
						{descOpen || !descLong ? desc : desc.slice(0, DESC_LIMIT)}
						{descLong && (
							<span className="applyPointer" style={{ color: PURPLE, fontWeight: 500, marginLeft: "0.3rem" }} onClick={() => setDescOpen((v) => !v)}>
								{descOpen ? " Ver menos" : "... Ver mais"}
							</span>
						)}
					</p>
				</div>
			)}

			{/* Comodidades */}
			{(hotel.amenitiesGroups || []).length > 0 && (
				<div style={{ marginBottom: "1.25rem" }}>
					<h3 style={{ color: DARK, marginBottom: "0.4rem" }}>Comodidades</h3>
					<ul style={{ listStyle: "none", padding: 0, margin: 0, columns: hotel.amenitiesGroups.length > 4 ? 2 : 1, color: "#6B6B6B", lineHeight: "2" }}>
						{hotel.amenitiesGroups.map((a, i) => (
							<li key={i} style={{ textAlign: "left" }}><i className="pi pi-check" style={{ color: "#00A3FF", marginRight: "0.4rem" }} />{a.label || a.name || a}</li>
						))}
					</ul>
				</div>
			)}

			{/* O que há nos arredores (POIs) */}
			{pois.length > 0 && (
				<div style={{ marginBottom: "1.25rem" }}>
					<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.4rem" }}>
						<h3 style={{ color: DARK, margin: 0 }}>O que há nos arredores</h3>
						{center && <Button label="Ver no mapa" icon="pi pi-map" text size="small" onClick={() => setPoisOpen(true)} />}
					</div>
					<ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
						{pois.slice(0, 5).map((p, i) => (
							<li key={p.poiid_ppn || i} style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.3rem 0", color: "#6B6B6B" }}>
								<i className="pi pi-map-marker" style={{ color: "#00A3FF" }} />
								<span style={{ flex: 1 }}>{p.poi_name || p.name}</span>
								{p.distancia != null && <span style={{ color: "#00A3FF" }}>{Number(p.distancia).toFixed(2)} km</span>}
							</li>
						))}
					</ul>
				</div>
			)}

			{/* Localização (mapa) */}
			{center && (
				<div>
					<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.4rem" }}>
						<h3 style={{ color: DARK, margin: 0 }}>Localização</h3>
						<Button label="Ampliar" icon="pi pi-search-plus" text size="small" onClick={() => setMapOpen(true)} />
					</div>
					{hotel.address && <div style={{ color: "#6B6B6B", marginBottom: "0.5rem" }}>{hotel.address}</div>}
					<div style={{ height: "300px", borderRadius: "12px", overflow: "hidden" }}>
						<APIProvider apiKey={env.GOOGLE_MAPS_API_KEY}>
							<Map defaultCenter={center} defaultZoom={15} gestureHandling="cooperative">
								<Marker position={center} />
							</Map>
						</APIProvider>
					</div>
				</div>
			)}

			{/* Zoom do mapa */}
			{center && (
				<Dialog header="Localização" visible={mapOpen} onHide={() => setMapOpen(false)} style={{ width: "min(1000px, 96vw)", height: "80vh" }} dismissableMask>
					<div style={{ width: "100%", height: "100%" }}>
						<APIProvider apiKey={env.GOOGLE_MAPS_API_KEY}>
							<Map defaultCenter={center} defaultZoom={16} gestureHandling="greedy">
								<Marker position={center} />
							</Map>
						</APIProvider>
					</div>
				</Dialog>
			)}

			{/* Arredores — mapa com marcadores + lista clicável */}
			{center && pois.length > 0 && (
				<Dialog header="O que há nos arredores" visible={poisOpen} onHide={() => { setPoisOpen(false); setSelectedPoi(null); }} style={{ width: "min(1000px, 96vw)", height: "85vh" }} dismissableMask>
					<div style={{ display: "flex", gap: "1rem", height: "100%", flexWrap: "wrap" }}>
						<div style={{ flex: "1 1 240px", overflowY: "auto", maxHeight: "100%" }}>
							{pois.map((p, i) => (
								<div key={p.poiid_ppn || i} onClick={() => setSelectedPoi(p)} style={{ display: "flex", gap: "0.5rem", alignItems: "center", padding: "0.5rem", cursor: "pointer", borderRadius: "8px", background: selectedPoi === p ? "#F2F4F7" : "transparent" }}>
									<i className="pi pi-map-marker" style={{ color: "#00A3FF" }} />
									<span style={{ flex: 1 }}>{p.poi_name || p.name}</span>
									{p.distancia != null && <span style={{ color: "#00A3FF" }}>{Number(p.distancia).toFixed(2)} km</span>}
								</div>
							))}
						</div>
						<div style={{ flex: "2 1 360px", minHeight: "300px" }}>
							<APIProvider apiKey={env.GOOGLE_MAPS_API_KEY}>
								<Map defaultCenter={center} defaultZoom={14} gestureHandling="greedy">
									<Marker position={center} />
									{pois.filter((p) => p.latitude != null && p.longitude != null).map((p, i) => (
										<Marker key={p.poiid_ppn || i} position={{ lat: Number(p.latitude), lng: Number(p.longitude) }} onClick={() => setSelectedPoi(p)} />
									))}
									{selectedPoi && selectedPoi.latitude != null && (
										<InfoWindow position={{ lat: Number(selectedPoi.latitude), lng: Number(selectedPoi.longitude) }} onCloseClick={() => setSelectedPoi(null)}>
											<div style={{ fontWeight: 600, color: DARK }}>{selectedPoi.poi_name || selectedPoi.name}</div>
										</InfoWindow>
									)}
								</Map>
							</APIProvider>
						</div>
					</div>
				</Dialog>
			)}

			{/* Visualizador de PDF (desktop) */}
			<Dialog header="PDF do hotel" visible={pdfOpen} onHide={closePdfViewer} style={{ width: "min(900px, 96vw)", height: "90vh" }} maximizable dismissableMask>
				{pdfUrl ? <iframe title="PDF do hotel" src={pdfUrl} style={{ width: "100%", height: "100%", border: "none" }} /> : null}
			</Dialog>
		</Shell>
	);
};

export default HotelViewPublic;
